import React from "react";
import {
  IdentifiableAnswerV3Input,
  LocalizedParticipantQuestion,
  QuestionType,
} from "../../../apis/API";
import BoolQuestion from "../BoolQuestion";
import TextQuestion from "../TextQuestion";
import WorkflowQuestion, { WorkflowAnswer } from "../workflow/WorkflowQuestion";

export interface QuestionDispatcherProps {
  question: LocalizedParticipantQuestion;
  /** Prior answer for this question, to re-display on resume / back. */
  defaultAnswer?: IdentifiableAnswerV3Input;
  /** Raw prepopulation `values` JSON for this question's workflow (on-file data). */
  prepopulationValues?: string;
  /** Normalized answer callback (engine's recordAnswer). */
  onAnswer: (answer: IdentifiableAnswerV3Input) => void;
}

export const normalizeWorkflow = (
  questionId: string,
  answer: WorkflowAnswer,
): IdentifiableAnswerV3Input => {
  if (answer.skipped) {
    return { questionId, questionType: QuestionType.workflow, skipped: true };
  }
  return {
    questionId,
    questionType: QuestionType.workflow,
    answer: answer.answer ?? null,
    workflowName: answer.workflowName,
    workflowAnswers: answer.workflowAnswers,
  };
};

/**
 * Renders the current question with the component matching its `questionType`
 * and normalizes the component's answer into `IdentifiableAnswerV3Input` for the
 * flow engine. Only supported questions reach here (the engine skips the rest).
 */
const QuestionDispatcher: React.FC<QuestionDispatcherProps> = ({
  question,
  defaultAnswer,
  prepopulationValues,
  onAnswer,
}) => {
  const questionId = question.id ?? "";

  switch (question.questionType) {
    case QuestionType.bool:
      return (
        <BoolQuestion
          question={question}
          defaultAnswer={defaultAnswer?.answer ?? undefined}
          defaultNotes={defaultAnswer?.notes ?? undefined}
          onAnswer={(a) =>
            onAnswer({
              questionId,
              answer: a.answer,
              notes: a.notes,
              questionType: QuestionType.bool,
            })
          }
        />
      );
    case QuestionType.text:
      return (
        <TextQuestion
          question={question}
          defaultValue={defaultAnswer?.textValue ?? undefined}
          onAnswer={(a) =>
            onAnswer({
              questionId,
              textValue: a.textValue,
              questionType: QuestionType.text,
            })
          }
        />
      );
    case QuestionType.workflow:
      return (
        <WorkflowQuestion
          question={question}
          defaultAnswer={defaultAnswer}
          prepopulationValues={prepopulationValues}
          onAnswer={(a) => onAnswer(normalizeWorkflow(questionId, a))}
        />
      );
    default:
      return null;
  }
};

export default QuestionDispatcher;
